import { Component, Input, OnInit } from '@angular/core';
import { AutoRentalInfo } from '../../../../../models/api/view/claim/details/auto/auto-rental-info';
import { BaseComponent } from '../../../../base/base.component';
import { AddressLabelComponent } from '../../../../core/labels/address-label.component';
import { CardComponent } from '../../../../core/panels/card.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';

@Component({
  selector: 'app-rental-info-au',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    AddressLabelComponent,
  ],
  template: `
    <div class="tw-text-md tw-italic tw-pt-3 tw-pb-6">
      This screen contains information on rental vehicles involved in the
      accident. This information is not used to track rental vehicles used for
      repairs or total losses after the event occurred.
    </div>
    <div class="tw-flex tw-w-full tw-gap-8">
      <div class="tw-basis-1/3 tw-flex tw-flex-col tw-gap-8">
        <app-card label="Rental vehicle">
          <app-detail-panel [dataSource]="rentalVehicleData">
            <app-detail-item label="year" name="year" />
            <app-detail-item label="make" name="make" />
            <app-detail-item label="model" name="model" />
            <app-detail-item label="VIN" name="vin" labelCase="upper" />
            <app-detail-item
              label="Rental VIN verified"
              name="vinVerified"
              labelCase="none"
            />
            <app-detail-item
              label="Used for rideshare"
              name="usedForRideshare"
            />
            <app-detail-item label="rideshare period" name="ridesharePeriod" />
            <app-detail-item label="mileage in" name="mileageIn" />
            <app-detail-item label="mileage out" name="mileageOut" />
          </app-detail-panel>
        </app-card>
        <app-card label="Renter">
          <app-detail-panel [dataSource]="renterData">
            <app-detail-item label="name" name="name" />
            <app-detail-item label="address" name="address">
              <ng-template #labelTemplate let-value>
                <app-address-label
                  [street1]="value"
                  [street2]="renterData.addressLine2"
                  [city]="renterData.cityStateZip"
                  [country]="renterData.country"
                />
              </ng-template>
            </app-detail-item>
            <app-detail-item label="work phone" name="workPhone" format="p" />
            <app-detail-item label="home phone" name="homePhone" format="p" />
            <app-detail-item label="cell phone" name="cellPhone" format="p" />
            <app-detail-item
              label="Member ID"
              name="memberId"
              labelCase="none"
            />
          </app-detail-panel>
        </app-card>
      </div>

      <div class="tw-basis-1/3 tw-flex tw-flex-col tw-gap-8">
        <app-card label="Rental Agreement">
          <app-detail-panel [dataSource]="rentalAgreementData">
            <app-detail-item label="rental company" name="rentalCompany" />
            <app-detail-item
              label="contract agreement number"
              name="contractAgreement"
            />
            <app-detail-item
              label="collision damage waiver purchased"
              name="collisionDamageWaiverPurchased"
            />
            <app-detail-item
              label="liability insurance purchased"
              name="liabilityInsurancePurchased"
            />
            <app-detail-item
              label="check out date"
              name="checkOutDate"
              format="d"
            />
            <app-detail-item
              label="check out location"
              name="checkOutLocation"
            />
            <app-detail-item
              label="check in date"
              name="checkInDate"
              format="d"
            />
            <app-detail-item label="check in location" name="checkInLocation" />
            <app-detail-item
              label="rental contract state"
              name="rentalContractState"
            />
            <app-detail-item
              label="corporate coverage"
              name="corporateCoverage"
            />
            <app-detail-item
              label="corporate contract number"
              name="corporateContract"
            />
          </app-detail-panel>
        </app-card>
        <app-card label="Other">
          <app-detail-panel [dataSource]="otherData">
            <app-detail-item label="other" name="other" />
            <app-detail-item label="notes" name="notes" />
          </app-detail-panel>
        </app-card>
      </div>
      <div class="tw-basis-1/3 tw-flex tw-flex-col tw-gap-8">
        <app-card label="Rental Vehicle Driver">
          <app-detail-panel [dataSource]="rentalDriverData">
            <app-detail-item label="name" name="name" />
            <app-detail-item label="address" name="address">
              <ng-template #labelTemplate let-value>
                <app-address-label
                  [street1]="value"
                  [street2]="rentalDriverData.addressLine2"
                  [city]="rentalDriverData.cityStateZip"
                  [country]="rentalDriverData.country"
                />
              </ng-template>
            </app-detail-item>
            <app-detail-item label="work phone" name="workPhone" format="p" />
            <app-detail-item label="home phone" name="homePhone" format="p" />
            <app-detail-item label="cell phone" name="cellPhone" format="p" />
            <app-detail-item label="employee loss" name="employeeLoss" />
            <app-detail-item
              label="not listed as additional driver"
              name="driverNotListed"
            />
          </app-detail-panel>
        </app-card>
        <app-card label="Driver insurance">
          <app-detail-panel [dataSource]="driverInsuranceData">
            <app-detail-item
              label="insurance company"
              name="insuranceCompany"
            />
            <app-detail-item
              label="insurance claim number"
              name="insuranceClaimNumber"
            />
            <app-detail-item
              label="insurance contact name"
              name="insuranceContactName"
            />
            <app-detail-item
              label="insurance contact phone"
              name="insuranceContactPhone"
              format="p"
            />
          </app-detail-panel>
        </app-card>
      </div>
    </div>
  `,
  styles: ``,
})
export class RentalInfoAuComponent extends BaseComponent implements OnInit {
  @Input() rentalInfo: AutoRentalInfo;

  get rentalVehicleData() {
    const vehicle = this.rentalInfo?.rentalVehicle || {};
    return {
      ...vehicle,
      vinVerified:
        vehicle.vinVerified === 'Y'
          ? 'Yes'
          : vehicle.vinVerified === 'N'
            ? 'No'
            : vehicle.vinVerified,
    };
  }

  get rentalAgreementData() {
    return this.rentalInfo?.rentalAgreement || {};
  }

  get renterData() {
    return this.rentalInfo?.renter || {};
  }

  get rentalDriverData() {
    return this.rentalInfo?.rentalDriver || {};
  }

  get driverInsuranceData() {
    return this.rentalInfo?.driverInsurance || {};
  }

  ngOnInit(): void {
    this.view.hideLoading();
  }

  get otherData() {
    return this.rentalInfo?.otherInfo || {};
  }
}
